import { CLOUDFRONT_BASE_URL, VIDEO_CDN_BASE_URL } from "../../config";

const S3_MEDIA_HOSTS = new Set([
  "new-hotplevideos.s3.amazonaws.com",
  "new-hotplevideos.s3.ap-northeast-2.amazonaws.com",
]);

const isAbsoluteUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return /^https?:\/\//i.test(url);
};

const isDataUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return /^data:/i.test(url);
};

const getCdnBaseUrl = () => CLOUDFRONT_BASE_URL?.replace(/\/$/, "") || "";
const getVideoCdnBaseUrl = () => VIDEO_CDN_BASE_URL?.replace(/\/$/, "") || "";

const isR2VideoPath = (path) => /^videos\//i.test(path || "");

const isImageMedia = (media, path) =>
  media?.mediaType?.includes("image") ||
  /\.(avif|gif|jpe?g|png|webp)(\?|#|$)/i.test(path || "");

const POST_IMAGE_PATH_PREFIXES = ["uploads/post/"];

const getS3CdnUrl = (url) => {
  if (!url || !getCdnBaseUrl()) return "";

  try {
    const parsedUrl = new URL(url);
    if (!S3_MEDIA_HOSTS.has(parsedUrl.hostname)) return "";
    return `${getCdnBaseUrl()}${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return "";
  }
};

/**
 * Normalizes a media path so that Next/Image receives an absolute URL.
 * Falls back to leading slash if CLOUDFRONT_BASE_URL is not defined.
 */
export const getMediaUrl = (path) => {
  if (!path || typeof path !== "string") return "";

  if (isDataUrl(path)) {
    return path;
  }

  const cdnUrl = getS3CdnUrl(path);
  if (cdnUrl) return cdnUrl;

  if (isAbsoluteUrl(path)) {
    return path;
  }

  const trimmedPath = path.startsWith("/") ? path.slice(1) : path;

  if (isR2VideoPath(trimmedPath) && VIDEO_CDN_BASE_URL) {
    return `${getVideoCdnBaseUrl()}/${trimmedPath}`;
  }

  if (CLOUDFRONT_BASE_URL) {
    return `${getCdnBaseUrl()}/${trimmedPath}`;
  }

  // Ensure the relative path starts with a slash so Next/Image accepts it
  return `/${trimmedPath}`;
};

export const getMediaRecordPath = (
  media,
  { preferPlaceholder = false, variant } = {}
) => {
  if (!media || typeof media !== "object") return "";

  const primaryPath =
    media.filePath || media.mediaPath || media.coverPath || media.url || "";

  if (variant === "thumbnail") {
    // Quality fix (2026-07-22): placeholderPath is a tiny over-compressed blur-up
    // (e.g. 1496px @ 23KB) and must NEVER be returned as the displayed image ahead of
    // a real one. Variants (thumb/medium/large) were never backfilled for bulk places,
    // so fall through to the full filePath (primaryPath) and use placeholder only as a
    // last resort. TODO: run backfill-image-variants.js to generate proper light thumbs.
    if (media.thumbnailPath) return media.thumbnailPath;
    if (media.mediumPath) return media.mediumPath;
    if (media.largePath) return media.largePath;
    if (isImageMedia(media, primaryPath) && primaryPath) {
      return primaryPath;
    }
    return media.placeholderPath || media.placeholder || primaryPath;
  }

  if (variant === "medium") {
    return media.mediumPath || media.largePath || media.thumbnailPath || primaryPath;
  }

  if (variant === "large") {
    return media.largePath || media.mediumPath || media.thumbnailPath || primaryPath;
  }

  if (!preferPlaceholder) {
    return primaryPath;
  }

  return media.thumbnailPath || media.placeholderPath || media.placeholder || primaryPath;
};

export const getMediaRecordUrl = (media, options) =>
  getMediaUrl(getMediaRecordPath(media, options));

export const PLACE_IMAGE_FALLBACK = "/person.png";

export const hasUsableMediaRecordPath = (media) =>
  Boolean(
    media?.mediaPath ||
      media?.filePath ||
      media?.url ||
      media?.thumbnailPath ||
      media?.mediumPath ||
      media?.largePath
  );

export const normalizeMediaRecordForGrid = (media) => ({
  ...media,
  type: media?.type || "media",
  mediaPath: media?.mediaPath || media?.filePath || media?.url,
});

export const getMediaRecordDedupKey = (media) => {
  if (!media || typeof media !== "object") return "";

  const preferredPath =
    getMediaRecordPath(media, { variant: "large" }) ||
    getMediaRecordPath(media) ||
    getMediaRecordPath(media, { variant: "thumbnail" });

  if (preferredPath) return preferredPath.trim().toLowerCase();

  return String(media?.id || media?._id || "").trim().toLowerCase();
};

export const dedupeMediaRecords = (mediaRecords = []) => {
  if (!Array.isArray(mediaRecords)) return [];

  const seen = new Set();
  return mediaRecords.filter((media) => {
    if (!hasUsableMediaRecordPath(media)) return false;

    const key = getMediaRecordDedupKey(media);
    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

export const getPlaceFallbackMediaRecords = (place) => {
  if (!place || typeof place !== "object") return [];

  return dedupeMediaRecords(
    [place.firstimage, place.firstimage2]
      .filter(Boolean)
      .map((url, index) => ({
        type: "media",
        mediaPath: url,
        mediaType: "image/jpeg",
        source: "place-photo",
        sortOrder: index,
      }))
  );
};

export const getPlaceImageUrl = (
  media,
  { variant = "thumbnail", fallback = PLACE_IMAGE_FALLBACK } = {}
) =>
  getMediaRecordUrl(media, variant ? { variant } : undefined) ||
  getMediaRecordUrl(media) ||
  fallback;
